"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Progress } from "../../ui/progress";
import { Checkbox } from "../../ui/checkbox";
import { ArrowLeft, Play, Loader2, AlertTriangle, Download, CheckCircle, MessageSquare, Save } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

interface TradePayableExceptionTestingProps {
  onBack?: () => void;
}

const EXCEPTIONS = [
  { id: 1, description: "Same vendor code with different vendor names" },
  { id: 2, description: "Same vendor name with different vendor codes" },
  { id: 3, description: "GSTIN where 14th character is not 'Z'" },
  { id: 4, description: "Vendor names not having GSTIN" },
  { id: 5, description: "Vendor names not having PAN" },
  { id: 6, description: "Vendor names not having vendor code" },
  { id: 7, description: "Vendor codes not having vendor name" },
  { id: 8, description: "PAN not matching GSTIN PAN component" },
  { id: 9, description: "Vendors in Trade Payable Register but not in Vendor Master" },
];

export default function TradePayableExceptionTesting({ onBack }: TradePayableExceptionTestingProps) {
  const [selectedExceptions, setSelectedExceptions] = useState<number[]>([]);
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `TRADE_PAYABLES_EXCEPTION_TESTING_${normalizedSection}`;
  };
  
  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_TP_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_TP_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "EXCEPTION-TESTING-TP-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  useEffect(() => {
    // On mount, load previously saved exception selections for Trade Payables
    (async () => {
      try {
        if (window.sharePointAPI?.loadExceptionList) {
          const result = await window.sharePointAPI.loadExceptionList("tp");
          if (result?.success && Array.isArray(result.data)) {
            setSelectedExceptions(result.data as number[]);
          }
        }
      } catch {}
    })();
  }, []);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "completed" | "error">("idle");

  const handleExceptionToggle = (exceptionId: number) => {
    setSelectedExceptions(prev => 
      prev.includes(exceptionId) 
        ? prev.filter(id => id !== exceptionId) 
        : [...prev, exceptionId]
    );
  };

  const handleSelectAllExceptions = () => {
    const allSelected = selectedExceptions.length === EXCEPTIONS.length;
    if (allSelected) {
      setSelectedExceptions([]);
    } else {
      setSelectedExceptions(EXCEPTIONS.map(exp => exp.id));
    }
  };

  const handleSaveExceptions = async () => {
    if (selectedExceptions.length === 0) {
      alert("Please select at least one exception to save");
      return;
    }

    setSaveStatus("saving");
    try {
      if (!window.sharePointAPI?.saveExceptionList) {
        throw new Error("SharePoint API method 'saveExceptionList' is not available. Please restart the application.");
      }

      const result = await window.sharePointAPI.saveExceptionList(selectedExceptions, "tp");

      if (result.success) {
        setSaveStatus("completed");
        alert(`Successfully saved ${selectedExceptions.length} exception(s) to Exception_Test.json`);
        setTimeout(() => setSaveStatus("idle"), 3000);
      } else {
        setSaveStatus("error");
        alert(`Failed to save exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      setSaveStatus("error");
      console.error("Error saving exceptions:", error);
      alert(`Failed to save exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleLoadExceptions = async () => {
    try {
      if (!window.sharePointAPI?.loadExceptionList) {
        alert("SharePoint API method 'loadExceptionList' is not available. Please restart the application.");
        return;
      }
      const result = await window.sharePointAPI.loadExceptionList("tp");
      if (result.success) {
        const selectedIds = Array.isArray(result.data) ? result.data : [];
        setSelectedExceptions(selectedIds);
        alert(`Loaded ${selectedIds.length} exception(s) from Exception_Test.json`);
      } else {
        alert(`Failed to load exceptions: ${result.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error loading exceptions:", error);
      alert(`Failed to load exceptions: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const canRunExceptionTests = () => selectedExceptions.length > 0;

  const runExceptionTests = async () => {
    if (!canRunExceptionTests()) return;
    
    // Trigger the same functionality as "Save Exceptions" before running tests
    try {
      await handleSaveExceptions();
    } catch (e) {
      console.warn("Saving exceptions before run failed, continuing with run", e);
    }
    
    setProcessingStatus("running");
    setProgress(0);
    setFileUrl("");

    try {
      console.log(" Starting Exception Testing for Trade Payables with exceptions:", selectedExceptions);
      
      // Use the existing payroll API with updated parameters
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_trdpbl_exception_testing", {
          inputFiles: [],
          options: {
            field_name_cm: "Execution_TP_VM",
            field_name_tr: "Execution_TP_TPR",
            expn_no: selectedExceptions,
          },
        });

        if (result.ok) {
          setProcessingStatus("completed");
        } else {
          console.error(" Payroll API call failed:", result.error);
          setProcessingStatus("error");
        }
      } else {
        console.error(" Payroll API not available");
        setProcessingStatus("error");
      }
    } catch (e) {
      console.error("Error running exception tests:", e);
      setProcessingStatus("error");
    }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "TP";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "EXCEPTION-TESTING-TP-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Exception Testing - Trade Payables</h2>
          <p className="text-gray-400">Select exceptions to test and run analysis</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              Exception Tests
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Exception Tests"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSelectAllExceptions}
                className="border-white/10"
              >
                {selectedExceptions.length === EXCEPTIONS.length ? "Deselect All" : "Select All"}
              </Button>
              <span className="rounded bg-blue-500/20 px-2 py-1 text-xs text-blue-300">
                {selectedExceptions.length} selected
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded border border-white/10 bg-white/5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 text-white">Sl. No.</TableHead>
                  <TableHead className="text-white">Exception</TableHead>
                  <TableHead className="w-40 text-white">Selection</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {EXCEPTIONS.map((exception) => (
                  <TableRow key={exception.id}>
                    <TableCell className="text-white">{exception.id}</TableCell>
                    <TableCell className="text-white">{exception.description}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`exception-${exception.id}`}
                          checked={selectedExceptions.includes(exception.id)}
                          onCheckedChange={() => handleExceptionToggle(exception.id)}
                        />
                        <Label htmlFor={`exception-${exception.id}`} className="text-white">
                          {selectedExceptions.includes(exception.id) ? "Selected" : "Select"}
                        </Label>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white">
              <Play className="h-5 w-5" />
              Execute Exception Tests
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Execute Exception Tests"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Run selected exception tests on the trade payables register
              </p>
              {!canRunExceptionTests() && (
                <p className="mt-1 text-xs text-red-400">
                  Please select at least one exception test
                </p>
              )}
            </div>
            <Button
              onClick={runExceptionTests}
              disabled={!canRunExceptionTests() || processingStatus === "running"}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {processingStatus === "running" ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Exception Tests
                </>
              )}
            </Button>
          </div>

          {/* Processing Indicator */}
          {processingStatus === "running" && (
            <div className="flex items-center gap-2 text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Processing Exception Tests...</span>
            </div>
          )}

          {/* Results Summary */}
          {processingStatus === "completed" && (
            <div className="space-y-4">
              <div className="rounded border border-green-500/20 bg-green-500/10 p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <h4 className="font-medium text-green-300">Exception Testing Completed</h4>
                    <p className="text-sm text-green-200">
                      All selected tests have been executed successfully
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {processingStatus === "error" && (
            <div className="rounded border border-red-500/20 bg-red-500/10 p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="font-medium text-red-300">Exception Testing Failed</h4>
                  <p className="text-sm text-red-200">
                    An error occurred while running the exception tests. Please check your data and try again.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="trade-payables"
            itemId="EXCEPTION-TESTING-TP-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Exception Tests")]: "Exception Tests",
              [getSectionQuestionId("Execute Exception Tests")]: "Execute Exception Tests",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
